/**
 * Metrics Service
 * Web-compatible service for calculating and retrieving user metrics
 * Includes Oracle scores, weekly review metrics, and habit statistics
 */

import { getSupabase } from '@/lib/db/supabase';
import type { Ritual } from './ritual';
import type { HabitCompletion } from './habits';

export interface OracleMetrics {
  clarity?: number;
  composure?: number;
  compassion?: number;
  command?: number;
  overall?: number;
}

export interface WeeklyMetrics {
  rituals_completed: number;
  total_ritual_time_minutes: number;
  habits_completed: number;
  current_streak: number;
  oracle_scores: OracleMetrics;
  top_virtues: Array<{ virtue: string; count: number }>;
  habit_completion_rate: number;
}

export interface RitualStats {
  total_rituals: number;
  current_streak: number;
  longest_streak: number;
  average_duration_minutes: number;
  mode_distribution: Record<string, number>;
}

/**
 * Calculate Oracle metrics from ritual data
 */
export function calculateOracleMetrics(ritual: Ritual): OracleMetrics {
  const metrics = ritual.oracle_metrics as OracleMetrics | undefined;

  if (!metrics) {
    return {};
  }

  // Calculate overall score as average of the four virtues
  const scores = [metrics.clarity, metrics.composure, metrics.compassion, metrics.command].filter(
    (s): s is number => typeof s === 'number'
  );

  const overall = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : undefined;

  return {
    ...metrics,
    overall,
  };
}

/**
 * Get weekly metrics for the current user
 */
export async function getWeeklyMetrics(startDate?: Date, endDate?: Date): Promise<WeeklyMetrics> {
  try {
    const supabase = getSupabase();
    if (!supabase) {
      // Supabase not configured - return defaults
      return {
        rituals_completed: 0,
        total_ritual_time_minutes: 0,
        habits_completed: 0,
        current_streak: 0,
        oracle_scores: {},
        top_virtues: [],
        habit_completion_rate: 0,
      };
    }

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      // Return default values when not authenticated
      return {
        rituals_completed: 0,
        total_ritual_time_minutes: 0,
        habits_completed: 0,
        current_streak: 0,
        oracle_scores: {},
        top_virtues: [],
        habit_completion_rate: 0,
      };
    }

    const start = startDate || new Date();
    start.setDate(start.getDate() - 7); // Default to last 7 days
    start.setHours(0, 0, 0, 0);

    const end = endDate || new Date();
    end.setHours(23, 59, 59, 999);

    const startISO = start.toISOString();
    const endISO = end.toISOString();

    // Get completed rituals in date range
    const { data: rituals, error: ritualsError } = await supabase
      .from('rituals')
      .select('*')
      .eq('user_id', user.id)
      .eq('ended', true)
      .not('completed_at', 'is', null)
      .gte('completed_at', startISO)
      .lte('completed_at', endISO);

    if (ritualsError) {
      console.warn('Error fetching rituals:', ritualsError.message);
      // Return defaults instead of throwing
      return {
        rituals_completed: 0,
        total_ritual_time_minutes: 0,
        habits_completed: 0,
        current_streak: 0,
        oracle_scores: {},
        top_virtues: [],
        habit_completion_rate: 0,
      };
    }

    // Get habit completions in date range
    const { data: completions, error: completionsError } = await supabase
      .from('habit_completions')
      .select('*')
      .eq('user_id', user.id)
      .gte('completed_at', startISO)
      .lte('completed_at', endISO);

    if (completionsError) {
      console.warn('Error fetching habit completions:', completionsError.message);
      // Continue with empty completions array
    }

    // Get current streak
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('current_streak')
      .eq('id', user.id)
      .single();

    if (profileError) {
      console.warn('Error fetching profile:', profileError.message);
    }

    const currentStreak = (profile?.current_streak as number) || 0;

    // Calculate Oracle scores (average across all rituals)
    const oracleScores: OracleMetrics = {
      clarity: 0,
      composure: 0,
      compassion: 0,
      command: 0,
    };

    let validScores = 0;
    const ritualList = (rituals || []) as Ritual[];

    ritualList.forEach((ritual) => {
      const metrics = calculateOracleMetrics(ritual);
      if (metrics.clarity !== undefined) {
        oracleScores.clarity = (oracleScores.clarity || 0) + metrics.clarity;
        validScores++;
      }
      if (metrics.composure !== undefined) {
        oracleScores.composure = (oracleScores.composure || 0) + metrics.composure;
      }
      if (metrics.compassion !== undefined) {
        oracleScores.compassion = (oracleScores.compassion || 0) + metrics.compassion;
      }
      if (metrics.command !== undefined) {
        oracleScores.command = (oracleScores.command || 0) + metrics.command;
      }
    });

    if (validScores > 0) {
      oracleScores.clarity = (oracleScores.clarity || 0) / validScores;
      oracleScores.composure = (oracleScores.composure || 0) / validScores;
      oracleScores.compassion = (oracleScores.compassion || 0) / validScores;
      oracleScores.command = (oracleScores.command || 0) / validScores;
      oracleScores.overall =
        ((oracleScores.clarity || 0) +
          (oracleScores.composure || 0) +
          (oracleScores.compassion || 0) +
          (oracleScores.command || 0)) /
        4;
    }

    // Calculate top virtues
    const virtueCounts: Record<string, number> = {};
    ritualList.forEach((ritual) => {
      if (ritual.virtue) {
        virtueCounts[ritual.virtue] = (virtueCounts[ritual.virtue] || 0) + 1;
      }
    });

    const topVirtues = Object.entries(virtueCounts)
      .map(([virtue, count]) => ({ virtue, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Calculate habit completion rate
    const { data: habits, error: habitsError } = await supabase
      .from('habits')
      .select('id')
      .eq('user_id', user.id);

    if (habitsError) {
      console.warn('Error fetching habits:', habitsError.message);
    }

    const totalHabits = (habits || []).length;
    const habitCompletionRate =
      totalHabits > 0 ? ((completions || []).length / (totalHabits * 7)) * 100 : 0;

    // Calculate total ritual time (estimate based on mode)
    const totalRitualTimeMinutes = ritualList.reduce((total, ritual) => {
      const modeMinutes = ritual.mode === 'quick' ? 15 : 30;
      return total + modeMinutes;
    }, 0);

    return {
      rituals_completed: ritualList.length,
      total_ritual_time_minutes: totalRitualTimeMinutes,
      habits_completed: (completions || []).length,
      current_streak: currentStreak,
      oracle_scores: oracleScores,
      top_virtues: topVirtues,
      habit_completion_rate: Math.min(100, Math.max(0, habitCompletionRate)),
    };
  } catch (error) {
    // Return default values on any error
    console.warn('Error fetching weekly metrics:', error);
    return {
      rituals_completed: 0,
      total_ritual_time_minutes: 0,
      habits_completed: 0,
      current_streak: 0,
      oracle_scores: {},
      top_virtues: [],
      habit_completion_rate: 0,
    };
  }
}

/**
 * Get ritual statistics for the current user
 */
export async function getRitualStats(): Promise<RitualStats> {
  try {
    const supabase = getSupabase();
    if (!supabase) {
      // Supabase not configured - return defaults
      return {
        total_rituals: 0,
        current_streak: 0,
        longest_streak: 0,
        average_duration_minutes: 0,
        mode_distribution: {},
      };
    }

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      // Return default values when not authenticated
      return {
        total_rituals: 0,
        current_streak: 0,
        longest_streak: 0,
        average_duration_minutes: 0,
        mode_distribution: {},
      };
    }

    // Get all completed rituals
    const { data: rituals, error } = await supabase
      .from('rituals')
      .select('*')
      .eq('user_id', user.id)
      .eq('ended', true)
      .not('completed_at', 'is', null)
      .order('completed_at', { ascending: false });

    if (error) {
      console.warn('Error fetching rituals:', error.message);
      // Return defaults instead of throwing
      return {
        total_rituals: 0,
        current_streak: 0,
        longest_streak: 0,
        average_duration_minutes: 0,
        mode_distribution: {},
      };
    }

    const ritualList = (rituals || []) as Ritual[];

    // Calculate streak
    let currentStreak = 0;
    let longestStreak = 0;
    let tempStreak = 0;
    let lastDate: Date | null = null;

    ritualList.forEach((ritual) => {
      if (!ritual.completed_at) return;

      const completedDate = new Date(ritual.completed_at);
      completedDate.setHours(0, 0, 0, 0);

      if (lastDate === null) {
        tempStreak = 1;
        longestStreak = 1;
      } else {
        const daysDiff = Math.floor(
          (lastDate.getTime() - completedDate.getTime()) / (1000 * 60 * 60 * 24)
        );

        if (daysDiff === 1) {
          tempStreak++;
          longestStreak = Math.max(longestStreak, tempStreak);
        } else if (daysDiff === 0) {
          // Same day, don't increment
        } else {
          tempStreak = 1;
        }
      }

      lastDate = completedDate;
    });

    // Get current streak from profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('current_streak')
      .eq('id', user.id)
      .single();

    if (profileError) {
      console.warn('Error fetching profile:', profileError.message);
    }

    currentStreak = (profile?.current_streak as number) || 0;

    // Calculate mode distribution
    const modeDistribution: Record<string, number> = {};
    ritualList.forEach((ritual) => {
      modeDistribution[ritual.mode] = (modeDistribution[ritual.mode] || 0) + 1;
    });

    // Calculate average duration (estimate)
    const totalMinutes = ritualList.reduce((total, ritual) => {
      const modeMinutes = ritual.mode === 'quick' ? 15 : 30;
      return total + modeMinutes;
    }, 0);

    const averageDurationMinutes =
      ritualList.length > 0 ? Math.round(totalMinutes / ritualList.length) : 0;

    return {
      total_rituals: ritualList.length,
      current_streak: currentStreak,
      longest_streak: longestStreak,
      average_duration_minutes: averageDurationMinutes,
      mode_distribution: modeDistribution,
    };
  } catch (error) {
    // Return default values on any error
    console.warn('Error fetching ritual stats:', error);
    return {
      total_rituals: 0,
      current_streak: 0,
      longest_streak: 0,
      average_duration_minutes: 0,
      mode_distribution: {},
    };
  }
}
